// src/components/dashboards/add_patient_v2/components/EnvChipSelector.tsx
// -----------------------------------------------------------------------------
// EnvChipSelector — V1-style "+ New Environment" pill that opens a
// multi-select dropdown.
//
// Visual reference: V1's "+ Add environment" chip in AddPatientForm.tsx —
// 24px tall, fontSize 0.72rem, info-tinted bg, primary on hover. Locked
// state shows a tiny lock icon when the user has hit their env quota.
//
// Behaviour:
//   - The trigger pill shows compact selection state (env name or "N envs").
//   - Clicking opens a popover (desktop) / fullscreen dialog (mobile).
//   - First row inside the dropdown is "+ Create new environment" (greyed
//     + locked when the user is at the tier env limit).
//   - All remaining rows are checkboxes; multi-select.
//
// Joan, 2026-05-27: replaces the larger 34px chip with the V1 pill size and
// adds the inline create entry so creating a new env doesn't require
// navigating away from the form.
// -----------------------------------------------------------------------------

import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  LockOutlined as LockOutlinedIcon,
  Search as SearchIcon,
  Workspaces as WorkspacesIcon,
} from "@mui/icons-material";
import React, { useMemo, useState } from "react";

import { useTranslation } from "../../../../i18n";
import { Environment } from "types";
import {
  athenaColors,
  athenaDarkActionsSx,
  athenaDarkContentSx,
  athenaDarkPaperSx,
  athenaDarkPrimaryButtonSx,
  athenaDarkSecondaryButtonSx,
  athenaDarkTitleSx,
} from "../../../layout/AthenaDialogStyles";

export interface EnvChipSelectorProps {
  availableEnvironments: Environment[];
  selectedEnvIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  /** Hit when the user picks "+ Create new environment" from the dropdown.
   *  Caller is responsible for opening a CreateEnvironmentModal. */
  onCreateEnvironmentClick?: () => void;
  /** When true the "+ Create new environment" row is locked with a tooltip. */
  atEnvironmentLimit?: boolean;
  /** Current user's ID — used to split the dropdown into "Envs I administer"
   *  (where the user appears in env.administrators or has OWNER/ADMIN role)
   *  vs "Other envs I'm in". */
  currentUserId?: string;
}

const EnvChipSelector: React.FC<EnvChipSelectorProps> = ({
  availableEnvironments,
  selectedEnvIds,
  onChange,
  disabled,
  onCreateEnvironmentClick,
  atEnvironmentLimit,
  currentUserId,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Free-text filter over env names. When active the list renders flat (no
  // sections / collapsed groups) so matches are immediately visible.
  const [query, setQuery] = useState("");
  // Which bracket-prefix groups (e.g. "[XXMM]") are expanded. Collapsed by
  // default so a large synced cohort doesn't flood the list.
  const [expandedTags, setExpandedTags] = useState<Set<string>>(new Set());
  const toggleTag = (key: string) =>
    setExpandedTags((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // Which consortium groups are expanded to reveal their child institutions.
  // Collapsed by default so a network with many centres stays compact; the
  // master checkbox on the header still batches every child at once.
  const [expandedConsortia, setExpandedConsortia] = useState<Set<string>>(
    new Set(),
  );
  const toggleConsortium = (key: string) =>
    setExpandedConsortia((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // cursor_logs/044: env ids reach this component with MIXED runtime types —
  // /environments/ serves numbers (despite the TS `id: string`), while ids
  // from the patient-suggestion / prefs paths are strings. All membership
  // checks therefore go through this String-coerced set (same convention as
  // PatientCard.tsx:377 and useEnvironmentFilter's Number()); strict
  // `.includes()` would render an activated env as unchecked.
  const selectedKeySet = useMemo(
    () => new Set(selectedEnvIds.map((id) => String(id))),
    [selectedEnvIds],
  );
  const isSelected = (envId: string | number) =>
    selectedKeySet.has(String(envId));

  // Consortium envs are network-wide: patients linked to them are VIEW-only
  // for member institutions and are not downloadable network-wide (export is
  // switched off at the consortium level; cursor_logs export-scope design).
  // Surface a subtle heads-up whenever the current selection includes one so
  // the clinician knows a consortium-linked patient won't be exportable by
  // member centers. Derived purely from `kind` — no backend flag added.
  const consortiumSelected = useMemo(
    () =>
      availableEnvironments.some(
        (e) => e.kind === "consortium" && selectedKeySet.has(String(e.id)),
      ),
    [availableEnvironments, selectedKeySet],
  );

  const chipLabel = useMemo(() => {
    if (selectedEnvIds.length === 0) {
      return t("patients_v2.env_chip_none", "Select environments");
    }
    const selectedEnvs = availableEnvironments.filter((e) =>
      selectedKeySet.has(String(e.id)),
    );
    if (selectedEnvs.length === 1) return selectedEnvs[0].name;
    if (selectedEnvs.length <= 3) {
      return selectedEnvs.map((e) => e.name).join(", ");
    }
    return t("patients_v2.env_chip_count", "{count} environments").replace(
      "{count}",
      String(selectedEnvs.length),
    );
  }, [availableEnvironments, selectedEnvIds, selectedKeySet, t]);

  const toggleEnv = (envId: string) => {
    if (isSelected(envId)) {
      onChange(selectedEnvIds.filter((id) => String(id) !== String(envId)));
    } else {
      onChange([...selectedEnvIds, envId]);
    }
  };

  const handleCreateClick = () => {
    if (atEnvironmentLimit || !onCreateEnvironmentClick) return;
    setAnchorEl(null);
    onCreateEnvironmentClick();
  };

  // V1-pill sizing (mirrors AddPatientForm.tsx:1576). Keep these in a
  // constant so the related "+ Add more details" / "+ Add another patient"
  // pills stay in lockstep visually.
  const pillSx = {
    height: 24,
    fontSize: "0.72rem",
    fontWeight: 500,
    color: theme.palette.info.main,
    bgcolor: alpha(theme.palette.info.main, 0.1),
    border: "1px solid transparent",
    backdropFilter: "blur(8px)",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.25s ease",
    "& .MuiChip-icon": {
      color: theme.palette.info.main,
      fontSize: 14,
      marginLeft: "8px",
      marginRight: "-2px",
    },
    "& .MuiChip-label": {
      paddingLeft: "8px",
      paddingRight: "10px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "&:hover": disabled
      ? undefined
      : {
          bgcolor: alpha(theme.palette.primary.main, 0.18),
          color: theme.palette.primary.main,
          borderColor: alpha(theme.palette.primary.main, 0.4),
          "& .MuiChip-icon": { color: theme.palette.primary.main },
        },
  } as const;

  const renderDropdownBody = (closeAfter = true) => (
    <Stack spacing={0.25} sx={{ minWidth: 280, maxWidth: 360 }}>
      {/* Consortium-only heads-up: a subtle, non-blocking note shown whenever
          the current selection includes a consortium env. */}
      {consortiumSelected && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.75,
            px: 1,
            py: 0.75,
            mb: 0.5,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.warning.main, 0.12),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
          }}
        >
          <InfoOutlinedIcon
            sx={{
              fontSize: 15,
              mt: "1px",
              color: theme.palette.warning.main,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              // The dark popover/dialog paper forces `.MuiTypography-root` to
              // white; override so the note keeps its amber warning tone.
              color: `${theme.palette.warning.main} !important`,
              lineHeight: 1.35,
              fontSize: "0.7rem",
            }}
          >
            {t(
              "patients_v2.env_consortium_view_only",
              "Patients added to a consortium environment are view-only across the network and cannot be downloaded by member institutions.",
            )}
          </Typography>
        </Box>
      )}

      {/* Search filter at the top — useful once there are many synced envs. */}
      <TextField
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("patients_v2.env_search_placeholder", "Search environments")}
        size="small"
        fullWidth
        autoFocus={!isMobile}
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 16, color: alpha(athenaColors.white, 0.6) }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 0.5,
          "& .MuiOutlinedInput-root": {
            color: athenaColors.white,
            fontSize: "0.82rem",
            "& fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.3) },
            "&:hover fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.5) },
            "&.Mui-focused fieldset": { borderColor: athenaColors.periwinkle },
          },
          "& .MuiOutlinedInput-input": {
            py: 0.6,
            color: athenaColors.white,
            "&::placeholder": { color: alpha(athenaColors.white, 0.55), opacity: 1 },
          },
        }}
      />

      {/* + Create new environment (V1 parity), below the search. */}
      {onCreateEnvironmentClick && (
        <>
          <Tooltip
            title={
              atEnvironmentLimit
                ? t(
                    "tier_gate.env_limit_reached",
                    "You have reached the maximum number of environments for your current plan. Upgrade to create more.",
                  )
                : ""
            }
            arrow
            placement="left"
            disableHoverListener={!atEnvironmentLimit}
          >
            <Box
              role="button"
              aria-disabled={atEnvironmentLimit ? true : undefined}
              onClick={() => {
                if (atEnvironmentLimit) return;
                handleCreateClick();
                if (closeAfter) setAnchorEl(null);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1,
                py: 0.75,
                minHeight: isMobile ? 44 : 36,
                borderRadius: 1,
                cursor: atEnvironmentLimit ? "not-allowed" : "pointer",
                opacity: atEnvironmentLimit ? 0.55 : 1,
                color: theme.palette.info.main,
                "&:hover": atEnvironmentLimit
                  ? undefined
                  : { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              {atEnvironmentLimit ? (
                <LockOutlinedIcon sx={{ fontSize: 14 }} />
              ) : (
                <AddIcon sx={{ fontSize: 14 }} />
              )}
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, fontSize: "0.82rem" }}
              >
                {t("patients.new_environment", "New Environment")}
              </Typography>
            </Box>
          </Tooltip>
          <Divider sx={{ my: 0.5 }} />
        </>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
          maxHeight: isMobile ? "none" : 360,
          overflowY: isMobile ? "visible" : "auto",
          overflowX: "hidden",
        }}
      >
      {(() => {
        // ── helpers ───────────────────────────────────────────────────────
        const isAdminOf = (env: Environment): boolean => {
          if (!currentUserId) return false;
          const admins = (env as Environment & {
            administrators?: { id: string }[];
          }).administrators;
          return (
            Array.isArray(admins) &&
            admins.some((u) => String(u.id) === String(currentUserId))
          );
        };

        // Free-text filter. When active we render a flat match list below.
        const q = query.trim().toLowerCase();
        const filtered = q
          ? availableEnvironments.filter((e) =>
              e.name.toLowerCase().includes(q),
            )
          : availableEnvironments;

        // Leading bracket tag, e.g. "[XXMM] Althaia" -> "[XXMM]". Used to fold
        // synced-cohort environments into collapsible groups.
        const bracketTag = (name: string): string | null => {
          const m = name.match(/^\s*(\[[^\]]+\])/);
          return m ? m[1] : null;
        };

        // Build consortium groups keyed by consortium id. A group's header is the
        // REAL consortium env when the user can see it; otherwise we SYNTHESIZE a
        // placeholder header from the children's shared parent_consortium_env
        // (+ parent_consortium_env_name). The consortium env is memberless (owned
        // by the XXMM sync) and therefore invisible to member-institution
        // clinicians, so keying the group off the parent env being PRESENT
        // silently dropped the whole network to a flat list for exactly the users
        // the grouping is meant to serve — the root cause of "the consortium isn't
        // even shown". Grouping by the id the children point at fixes it whether
        // or not the parent env itself is in the list.
        //
        // Real consortium envs the user can see, keyed by id.
        const consortiumById = new Map<string, Environment>();
        for (const e of filtered) {
          if (e.kind === "consortium") consortiumById.set(String(e.id), e);
        }
        // Institution envs bucketed by the consortium id they point at.
        const childrenByParent = new Map<string, Environment[]>();
        for (const e of filtered) {
          if (e.kind === "institution" && e.parent_consortium_env != null) {
            const pid = String(e.parent_consortium_env);
            const arr = childrenByParent.get(pid) ?? [];
            arr.push(e);
            childrenByParent.set(pid, arr);
          }
        }

        type Group = {
          consortium: Environment;
          institutions: Environment[];
          // True when `consortium` is a synthesized placeholder (the real env is
          // not in the user's list): its id must NOT enter the selection.
          synthesized: boolean;
        };
        const groups: Group[] = [];
        const groupedIds = new Set<string>();

        // Every consortium id that is either visible or referenced by a visible
        // child, in the order it first appears in the list (stable rendering).
        const consortiumOrder: string[] = [];
        const seenCid = new Set<string>();
        for (const e of filtered) {
          const cid =
            e.kind === "consortium"
              ? String(e.id)
              : e.kind === "institution" && e.parent_consortium_env != null
                ? String(e.parent_consortium_env)
                : null;
          if (cid && !seenCid.has(cid)) {
            seenCid.add(cid);
            consortiumOrder.push(cid);
          }
        }

        for (const cid of consortiumOrder) {
          const institutions = childrenByParent.get(cid) ?? [];
          const real = consortiumById.get(cid);
          // A synthesized group only makes sense when there are children to nest;
          // a visible consortium with no visible children still renders (header
          // with no chevron), matching the prior behaviour.
          if (!real && institutions.length === 0) continue;
          let consortium: Environment;
          if (real) {
            consortium = real;
            groupedIds.add(String(real.id));
          } else {
            const name =
              institutions[0]?.parent_consortium_env_name ||
              bracketTag(institutions[0]?.name ?? "") ||
              t("patients_v2.kind_consortium", "consortium");
            consortium = {
              id: cid,
              name,
              kind: "consortium",
              parent_consortium_env: null,
            } as Environment;
          }
          for (const inst of institutions) groupedIds.add(String(inst.id));
          groups.push({ consortium, institutions, synthesized: !real });
        }
        const standalone = filtered.filter(
          (e) => !groupedIds.has(String(e.id)),
        );

        // Split each section (groups + standalone) into admin vs other.
        const adminGroups = groups.filter((g) => isAdminOf(g.consortium));
        const otherGroups = groups.filter((g) => !isAdminOf(g.consortium));
        const adminStandalone = standalone.filter((e) => isAdminOf(e));
        const otherStandalone = standalone.filter((e) => !isAdminOf(e));

        const showSections =
          !!currentUserId &&
          adminStandalone.length + adminGroups.length > 0 &&
          otherStandalone.length + otherGroups.length > 0;

        const sectionLabelSx = {
          display: "block",
          mt: 0.5,
          mb: 0.25,
          ml: 1,
          color: theme.palette.primary.main,
          fontSize: "0.6rem",
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: "uppercase" as const,
        };
        const indentSx = { ml: 3 };
        const kindBadgeSx = {
          height: 16,
          fontSize: "0.6rem",
          fontWeight: 600,
          ml: 0.75,
          textTransform: "uppercase" as const,
        } as const;

        const renderRow = (env: Environment, indent = false) => {
          const kindLabel =
            env.kind === "consortium"
              ? t("patients_v2.kind_consortium", "consortium")
              : env.kind === "institution"
                ? t("patients_v2.kind_institution", "institution")
                : null;
          return (
            <FormControlLabel
              key={env.id}
              control={
                <Checkbox
                  size="small"
                  checked={isSelected(env.id)}
                  onChange={() => toggleEnv(env.id)}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {env.name}
                  </Typography>
                  {kindLabel && (
                    <Chip
                      label={kindLabel}
                      size="small"
                      sx={{
                        ...kindBadgeSx,
                        bgcolor: alpha(theme.palette.info.main, 0.12),
                        color: theme.palette.info.main,
                      }}
                    />
                  )}
                </Box>
              }
              sx={{
                display: "flex",
                minHeight: isMobile ? 44 : 32,
                mx: 0,
                ...(indent ? indentSx : {}),
              }}
            />
          );
        };

        const renderGroup = (g: Group) => {
          const memberIds = g.institutions.map((i) => i.id);
          // A synthesized header is not a real env — never put its placeholder id
          // in the selection. A real consortium env is itself selectable
          // alongside its members.
          const allInGroupIds = g.synthesized
            ? memberIds
            : [g.consortium.id, ...memberIds];
          const allSelected =
            allInGroupIds.length > 0 &&
            allInGroupIds.every((id) => isSelected(id));
          const someSelected = allInGroupIds.some((id) => isSelected(id));
          const selectAllCount = g.synthesized
            ? g.institutions.length
            : g.institutions.length + 1;
          const toggleAll = () => {
            if (allSelected) {
              const groupKeys = new Set(allInGroupIds.map((id) => String(id)));
              onChange(
                selectedEnvIds.filter((id) => !groupKeys.has(String(id))),
              );
            } else {
              // Merge by String key so a mixed-type duplicate of the same env
              // can't end up in the selection twice.
              const merged = [...selectedEnvIds];
              for (const id of allInGroupIds) {
                if (!isSelected(id)) merged.push(id);
              }
              onChange(merged);
            }
          };
          const consortiumKey = String(g.consortium.id);
          const expanded = expandedConsortia.has(consortiumKey);
          const hasChildren = g.institutions.length > 0;
          return (
            <Box key={`group-${g.consortium.id}`}>
              {/* Consortium header: master select-all checkbox + collapse chevron.
                  The checkbox batches every child (and the consortium env) through
                  the same onChange; the chevron only toggles child visibility. */}
              <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={!allSelected && someSelected}
                      onChange={toggleAll}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          fontWeight: 600,
                          maxWidth: 180,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {g.consortium.name}
                      </Typography>
                      <Chip
                        label={t("patients_v2.kind_consortium", "consortium")}
                        size="small"
                        sx={{
                          ...kindBadgeSx,
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          color: theme.palette.primary.main,
                        }}
                      />
                      {hasChildren && (
                        <Typography
                          variant="caption"
                          sx={{ ml: 0.75, color: theme.palette.text.secondary }}
                        >
                          {t(
                            "patients_v2.consortium_select_all",
                            "Select all ({count})",
                          ).replace("{count}", String(selectAllCount))}
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{
                    display: "flex",
                    flex: 1,
                    minWidth: 0,
                    minHeight: isMobile ? 44 : 32,
                    mx: 0,
                  }}
                />
                {hasChildren && (
                  <IconButton
                    size="small"
                    onClick={() => toggleConsortium(consortiumKey)}
                    aria-label={
                      expanded
                        ? t(
                            "patients_v2.consortium_collapse",
                            "Hide institutions",
                          )
                        : t(
                            "patients_v2.consortium_expand",
                            "Show institutions",
                          )
                    }
                    sx={{ flexShrink: 0, color: theme.palette.text.secondary }}
                  >
                    {expanded ? (
                      <ExpandLessIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                )}
              </Box>
              <Collapse in={expanded} unmountOnExit>
                {g.institutions.map((inst) => renderRow(inst, true))}
              </Collapse>
            </Box>
          );
        };

        // Fold bracket-tagged envs (e.g. "[XXMM] …") into collapsible groups
        // so a large synced cohort doesn't flood the flat list. Non-tagged
        // envs render first; each tag becomes a collapsed header with a count.
        const renderFlatWithBracketGroups = (
          envs: Environment[],
          sectionKey: string,
        ) => {
          const nonTagged: Environment[] = [];
          const tagged = new Map<string, Environment[]>();
          for (const e of envs) {
            const tag = bracketTag(e.name);
            if (tag) {
              const arr = tagged.get(tag) ?? [];
              arr.push(e);
              tagged.set(tag, arr);
            } else {
              nonTagged.push(e);
            }
          }
          return (
            <>
              {nonTagged.map((e) => renderRow(e))}
              {Array.from(tagged.entries()).map(([tag, list]) => {
                const key = `${sectionKey}:${tag}`;
                const expanded = expandedTags.has(key);
                const selectedCount = list.filter((e) =>
                  isSelected(e.id),
                ).length;
                return (
                  <Box key={key}>
                    <Box
                      role="button"
                      onClick={() => toggleTag(key)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1,
                        py: 0.5,
                        minHeight: isMobile ? 44 : 32,
                        borderRadius: 1,
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                        },
                      }}
                    >
                      {expanded ? (
                        <ExpandLessIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tag}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        ({list.length})
                      </Typography>
                      {selectedCount > 0 && (
                        <Chip
                          label={selectedCount}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            ml: 0.25,
                            bgcolor: alpha(theme.palette.success.main, 0.18),
                            color: theme.palette.success.light,
                          }}
                        />
                      )}
                    </Box>
                    <Collapse in={expanded} unmountOnExit>
                      {list.map((e) => renderRow(e, true))}
                    </Collapse>
                  </Box>
                );
              })}
            </>
          );
        };

        const renderSection = (
          sectionGroups: Group[],
          sectionFlat: Environment[],
          sectionKey: string,
        ) => (
          <>
            {sectionGroups.map(renderGroup)}
            {renderFlatWithBracketGroups(sectionFlat, sectionKey)}
          </>
        );

        // Active search → flat list of matches (no sections, no collapsing) so
        // results are immediately visible.
        if (q) {
          if (filtered.length === 0) {
            return (
              <Typography
                variant="body2"
                sx={{ px: 1, py: 1, color: theme.palette.text.secondary }}
              >
                {t("patients_v2.env_search_no_results", "No environments match.")}
              </Typography>
            );
          }
          return <>{filtered.map((e) => renderRow(e))}</>;
        }

        if (!showSections) {
          return (
            <>
              {groups.map(renderGroup)}
              {renderFlatWithBracketGroups(standalone, "all")}
            </>
          );
        }
        return (
          <>
            <Typography variant="caption" sx={sectionLabelSx}>
              {t("patients_v2.env_section_admin", "Envs I administer")}
            </Typography>
            {renderSection(adminGroups, adminStandalone, "admin")}
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="caption" sx={sectionLabelSx}>
              {t("patients_v2.env_section_other", "Other envs I'm in")}
            </Typography>
            {renderSection(otherGroups, otherStandalone, "other")}
          </>
        );
      })()}
      </Box>
    </Stack>
  );

  return (
    <>
      <Chip
        icon={<WorkspacesIcon />}
        label={chipLabel}
        size="small"
        onClick={(e) => {
          if (disabled) return;
          setQuery("");
          setAnchorEl(e.currentTarget as HTMLElement);
        }}
        disabled={disabled}
        sx={{ ...pillSx, maxWidth: { xs: 220, sm: 320 } }}
        aria-label={t("patients_v2.env_chip_aria", "Select environments")}
      />

      {!isMobile && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                ...athenaDarkPaperSx,
                p: 1,
                mt: 0.5,
                // Tighten the dark dialog's table/divider treatments — the
                // popover body is a list, not a wizard, so the heavy borders
                // would over-decorate it.
                "& .MuiDivider-root": {
                  borderColor: alpha(athenaColors.periwinkle, 0.2),
                },
                "& .MuiFormControlLabel-label": {
                  color: athenaColors.white,
                },
                "& .MuiTypography-root": { color: athenaColors.white },
                "& .MuiTypography-colorTextSecondary": {
                  color: `${alpha(athenaColors.white, 0.6)} !important`,
                },
              },
            },
          }}
        >
          {renderDropdownBody()}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
            <Button
              size="small"
              onClick={() => setAnchorEl(null)}
              sx={athenaDarkSecondaryButtonSx}
            >
              {t("action.done", "Done")}
            </Button>
          </Box>
        </Popover>
      )}

      {isMobile && (
        <Dialog
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          fullScreen
          PaperProps={{ sx: athenaDarkPaperSx }}
        >
          <DialogTitle sx={athenaDarkTitleSx}>
            <Typography variant="h6" sx={{ flexGrow: 1, color: athenaColors.white }}>
              {t("patients_v2.env_chip_aria", "Select environments")}
            </Typography>
            <IconButton
              onClick={() => setAnchorEl(null)}
              size="small"
              sx={{ color: athenaColors.white }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={athenaDarkContentSx} dividers>
            {renderDropdownBody(false)}
          </DialogContent>
          <DialogActions sx={athenaDarkActionsSx}>
            <Button
              variant="contained"
              onClick={() => setAnchorEl(null)}
              sx={{ ...athenaDarkPrimaryButtonSx, minHeight: 44 }}
            >
              {t("action.done", "Done")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default EnvChipSelector;
